import * as React from "react";
import Alert, { AlertColor } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

type SnackbarContextValue = {
    showSnackbar: (message: string, severity?: AlertColor) => void;
};

const SnackbarContext = React.createContext<SnackbarContextValue | undefined>(undefined);

export const useSnackbar = () => {
    const context = React.useContext(SnackbarContext);

    if (!context) {
        throw new Error("useSnackbar must be used within SnackbarProvider");
    }

    return context;
};

type SnackbarProviderProps = {
    children: React.ReactNode;
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState("");
    const [severity, setSeverity] = React.useState<AlertColor>("info");

    const showSnackbar = React.useCallback((newMessage: string, newSeverity: AlertColor = "info") => {
        setMessage(newMessage);
        setSeverity(newSeverity);
        setOpen(true);
    }, []);

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setOpen(false);
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}

            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={handleClose}
                    severity={severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};